/* BEATSEEK — moderation queue for curated fingerprint entries (Community Edit Index).
   Curated entries name someone else's release, so they stay dormant until a moderator signs off. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` });
const fmtDur = (s) => {
  const n = Math.round(Number(s) || 0);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

export const CuratedIdQueue = ({ onReviewed }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('pending');
  const [pending, setPending] = useState(0);
  const [disputed, setDisputed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/community-index/review?status=${s}`, { headers: auth() });
      const d = await r.json();
      setItems(d.entries || []);
      setPending(d.pending || 0);
      setDisputed(d.disputed || 0);
    } catch {
      toast.error('Could not load the curated ID queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    let reason = '';
    if (action === 'reject') {
      reason = window.prompt('Reason (the submitter sees this — optional):') ?? null;
      if (reason === null) return;
    }
    setBusy(id);
    try {
      const body = new FormData();
      if (reason) body.append('reason', reason);
      const r = await fetch(`${API}/api/community-index/${id}/${action}`, { method: 'POST', headers: auth(), body });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Action failed');
      toast.success(action === 'approve' ? 'Approved — the engine will name it from now on' : 'Rejected — fingerprints deleted');
      load(status);
      onReviewed?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="curated-id-queue">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-amber-300">
          🧬 Index review{pending > 0 && <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-amber-200" data-testid="curated-queue-pending">{pending} waiting</span>}
          {disputed > 0 && <span className="ml-2 rounded-full bg-pink-500/20 px-2 py-0.5 text-pink-200" data-testid="curated-queue-disputed">{disputed} disputed</span>}
        </div>
        <div className="ml-auto flex gap-1">
          {['pending', 'disputed', 'approved', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`curated-queue-filter-${s}`}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold capitalize transition-colors ${
                status === s ? 'bg-amber-500/25 text-amber-200' : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-gray-500">
        <strong className="text-gray-400">Pending</strong> curated entries name a released track someone else made — they stay
        dormant until approved here. <strong className="text-gray-400">Disputed</strong> entries are crowd-locked IDs someone has
        reported as wrong: they stay live until you decide. Rejecting deletes the fingerprints and unlocks the ID.
      </p>

      {loading ? (
        <div className="py-6 text-center text-xs text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-white/5 bg-black/20 py-8 text-center text-xs text-gray-500" data-testid="curated-queue-empty">
          Nothing {status} right now.
        </div>
      ) : (
        items.map(e => (
          <div key={e.id} className="rounded-lg border border-amber-700/25 bg-amber-900/5 p-3" data-testid={`curated-queue-item-${e.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">
                  {e.artist} – {e.title}
                </div>
                <div className="mt-0.5 text-[11px] text-gray-400">
                  {(e.version_type || '').toUpperCase()}{e.genre ? ` · ${e.genre}` : ''} · {fmtDur(e.duration_s)} · {(e.hash_count || 0).toLocaleString()} fingerprints
                  {e.release_status ? ` · ${String(e.release_status).replace('_', ' ')}` : ''}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                  {e.entry_kind === 'confirmed'
                    ? <>crowd-locked · {e.confirm_count || 0} confirmations{e.disputes ? ` · ⚠️ ${e.disputes} dispute${e.disputes === 1 ? '' : 's'}` : ''}{e.locked_by_staff ? ' · locked by staff' : ''}</>
                    : <>submitted by <span className="text-cyan-400">@{e.username}</span> · {new Date(e.created_at).toLocaleString()}
                      {e.original_title ? ` · claims: ${e.original_title}` : ''}</>}
                </div>
                {e.link && (
                  <a href={e.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-[11px] text-cyan-400 hover:underline">
                    reference link ↗
                  </a>
                )}
                {e.reject_reason && <div className="mt-1 text-[11px] text-red-400">rejected: {e.reject_reason}</div>}
              </div>
              {(status === 'pending' || status === 'disputed') && (
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => act(e.id, 'approve')}
                    disabled={busy === e.id}
                    data-testid={`curated-queue-approve-${e.id}`}
                    className="rounded-md bg-emerald-600/20 px-3 py-1.5 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-600/35 disabled:opacity-50"
                  >
                    {status === 'disputed' ? 'Keep locked' : 'Approve'}
                  </button>
                  <button
                    onClick={() => act(e.id, 'reject')}
                    disabled={busy === e.id}
                    data-testid={`curated-queue-reject-${e.id}`}
                    className="rounded-md bg-red-600/20 px-3 py-1.5 text-xs font-semibold text-red-300 transition-colors hover:bg-red-600/35 disabled:opacity-50"
                  >
                    {status === 'disputed' ? 'Unlock' : 'Reject'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CuratedIdQueue;
